import React from "react";
import { HelpCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	CheckboxListSection,
	Section,
	toggleInGroup,
} from "@/components/Shared/Filters/filterControls";
import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import { JOB_STATUS } from "../../../configs/constants/JobStatus";
import { JobDateRelation, JobFilterDefinition, JobWhen } from "../../../models/JobFilter";
import { JobFilterOptions } from "./useJobFilterData";

interface Props {
	definition: JobFilterDefinition;
	onChange: (def: JobFilterDefinition) => void;
	options: JobFilterOptions;
}

const STATUS_OPTIONS: JOB_STATUS[] = [
	JOB_STATUS.OPEN,
	JOB_STATUS.BOOKED,
	JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS,
	JOB_STATUS.DECLINED,
	JOB_STATUS.CANCELLED,
];

/**
 * The Datum filter reads as a sentence: "visa jobb som {relation} {when}".
 * RELATION is *how* a job relates to the chosen date(s); WHEN is the date(s).
 */
const RELATION_OPTIONS: { value: JobDateRelation; labelKey: string; fallback: string }[] = [
	{ value: "ongoing", labelKey: "job.filters.relationOngoing", fallback: "pågår under perioden" },
	{ value: "booked", labelKey: "job.filters.relationBooked", fallback: "har bokade pass" },
	{ value: "open", labelKey: "job.filters.relationOpen", fallback: "har obemannade pass" },
	{ value: "any", labelKey: "job.filters.relationAny", fallback: "har något pass" },
];

/** "all" is the off state (no date constraint); the rest map onto `JobWhen`. */
type WhenKind = "all" | JobWhen["kind"];

const WHEN_OPTIONS: { value: WhenKind; labelKey: string; fallback: string }[] = [
	{ value: "all", labelKey: "job.filters.anyDate", fallback: "Alla datum" },
	{ value: "today", labelKey: "job.filters.today", fallback: "Idag" },
	{ value: "tomorrow", labelKey: "job.filters.tomorrow", fallback: "Imorgon" },
	{ value: "thisWeek", labelKey: "job.filters.thisWeek", fallback: "Denna vecka" },
	{ value: "next7days", labelKey: "job.filters.next7days", fallback: "Kommande 7 dagar" },
	{ value: "date", labelKey: "job.filters.specificDate", fallback: "Välj datum…" },
	{ value: "range", labelKey: "job.filters.dateRange", fallback: "Datumintervall…" },
];

/**
 * The Kompetenskrav section lists certifications and consultant tags in ONE
 * checkbox list. The definition keeps them in two arrays (they hit different
 * query params), so the option value carries a kind prefix — the same `c:` /
 * `t:` encoding as `requirementsKey` in JobRequirementsEditor.
 */
const REQ_CERT_PREFIX = "c:";
const REQ_TAG_PREFIX = "t:";

const SELECT_CLS =
	"rounded-md border border-solid border-gray-300 bg-white px-2 py-1 text-base text-gray-800";
const DATE_CLS = "rounded-md border border-solid border-gray-300 px-2 py-1 text-sm";

/**
 * The job set of filter controls (status, customers, contracts, pool, extent,
 * date). Reused by the job-list filter drawer and the org-defined job filters
 * settings editor. The date dimension is a relation + when sentence resolved to
 * query params at build time — see `definitionToJobParams`.
 */
const JobFilterBuilder: React.FC<Props> = ({ definition, onChange, options }) => {
	const { t } = useTranslation();

	const when = definition.when;
	const whenKind: WhenKind = when?.kind ?? "all";
	const relation: JobDateRelation = definition.dateRelation ?? "ongoing";

	const singleDate = when?.kind === "date" ? when.date : undefined;
	const rangeStart = when?.kind === "range" ? when.start : undefined;
	const rangeEnd = when?.kind === "range" ? when.end : undefined;

	const setRelation = (value: JobDateRelation) => onChange({ ...definition, dateRelation: value });

	const requirementOptions = [
		...options.requirementCertifications.map((cert) => ({
			value: REQ_CERT_PREFIX + cert.id,
			label: cert.name,
		})),
		...options.requirementTags.map((tag) => ({ value: REQ_TAG_PREFIX + tag.id, label: tag.name })),
	];
	const selectedRequirements = [
		...(definition.requirementCertifications ?? []).map((key) => REQ_CERT_PREFIX + key),
		...(definition.requirementTags ?? []).map((key) => REQ_TAG_PREFIX + key),
	];
	const toggleRequirement = (value: string) => {
		if (value.startsWith(REQ_CERT_PREFIX)) {
			onChange(
				toggleInGroup(definition, "requirementCertifications", value.slice(REQ_CERT_PREFIX.length))
			);
		} else {
			onChange(toggleInGroup(definition, "requirementTags", value.slice(REQ_TAG_PREFIX.length)));
		}
	};

	const setWhenKind = (kind: WhenKind) => {
		if (kind === "all") {
			// Drop the date constraint (and the now-irrelevant relation).
			onChange({ ...definition, when: undefined, dateRelation: undefined });
			return;
		}
		const next: JobWhen =
			kind === "date"
				? { kind: "date", date: singleDate }
				: kind === "range"
				? { kind: "range", start: rangeStart, end: rangeEnd }
				: { kind };
		onChange({ ...definition, when: next });
	};

	const setSingleDate = (date?: string) =>
		onChange({ ...definition, when: { kind: "date", date } });

	const setRange = (patch: { start?: string; end?: string }) =>
		onChange({
			...definition,
			when: { kind: "range", start: rangeStart, end: rangeEnd, ...patch },
		});

	return (
		<>
			<CheckboxListSection
				title={t("STATUS")}
				options={STATUS_OPTIONS.map((s) => ({
					value: s,
					label: t(("job.jobStatus." + s.toLowerCase()) as any),
				}))}
				selected={definition.status}
				onToggle={(value) => onChange(toggleInGroup(definition, "status", value))}
			/>

			<CheckboxListSection
				title={t("CUSTOMER")}
				divider
				options={options.customers.map((c) => ({ value: c.id, label: c.name }))}
				selected={definition.customerIds}
				onToggle={(value) => onChange(toggleInGroup(definition, "customerIds", value))}
			/>

			{/* Tags of the job's customer (customerTagTypes). Only rendered when the
			    org has defined customer tag types — CheckboxListSection renders
			    nothing for an empty option list. */}
			<CheckboxListSection
				title={t("job.filters.customerTags" as any, "Kundtaggar")}
				divider
				options={options.customerTags.map((tag) => ({ value: tag.id, label: tag.name }))}
				selected={definition.customerTags}
				onToggle={(value) => onChange(toggleInGroup(definition, "customerTags", value))}
			/>

			<CheckboxListSection
				title={t("CONTRACT" as any)}
				divider
				options={options.contracts.map((c) => ({ value: c.id, label: c.name }))}
				selected={definition.contractIds}
				onToggle={(value) => onChange(toggleInGroup(definition, "contractIds", value))}
			/>

			<CheckboxListSection
				title={t("POOL" as any)}
				divider
				options={options.pools.map((p) => ({ value: p.id, label: p.name }))}
				selected={definition.pools}
				onToggle={(value) => onChange(toggleInGroup(definition, "pools", value))}
			/>

			{/* Descriptive job tags (jobTagTypes) — not the matching requirements */}
			<CheckboxListSection
				title={t("job.filters.jobTags" as any, "Uppdragstaggar")}
				divider
				options={options.tags.map((tag) => ({ value: tag.id, label: tag.name }))}
				selected={definition.tags}
				onToggle={(value) => onChange(toggleInGroup(definition, "tags", value))}
			/>

			{/* The matching gate (job.requirements): certifications first, then
			    consultant tags, in one list. Hidden when the org has neither. */}
			<CheckboxListSection
				title={t("job.filters.requirements" as any, "Kompetenskrav")}
				divider
				options={requirementOptions}
				selected={selectedRequirements}
				onToggle={toggleRequirement}
			/>

			<Section
				title={t("job.filters.extent" as any, "Omfattning")}
				divider
				count={(definition.minDays != null ? 1 : 0) + (definition.minHours != null ? 1 : 0)}
			>
				<div className="flex gap-4">
					<div>
						<p className="mb-2 text-base font-semibold text-gray-800">
							{t("job.filters.minDays" as any, "Minst antal dagar")}
						</p>
						<input
							type="number"
							min={0}
							className="w-24 rounded-md border border-solid border-gray-300 px-2 py-1 text-base"
							value={definition.minDays ?? ""}
							onChange={(e) =>
								onChange({
									...definition,
									minDays: e.target.value === "" ? undefined : Number(e.target.value),
								})
							}
						/>
					</div>
					<div>
						<p className="mb-2 text-base font-semibold text-gray-800">
							{t("job.filters.minHours" as any, "Minst antal timmar")}
						</p>
						<input
							type="number"
							min={0}
							className="w-24 rounded-md border border-solid border-gray-300 px-2 py-1 text-base"
							value={definition.minHours ?? ""}
							onChange={(e) =>
								onChange({
									...definition,
									minHours: e.target.value === "" ? undefined : Number(e.target.value),
								})
							}
						/>
					</div>
				</div>
			</Section>

			<Section title={t("DATE" as any, "Datum")} divider count={definition.when ? 1 : 0}>
				<div className="mb-1 flex items-center gap-1 text-sm text-gray-600">
					<span>{t("job.filters.showJobsThat" as any, "Visa jobb som")}</span>
				</div>

				{/* Sentence: [ relation ] [ when ] */}
				<div className="flex flex-wrap items-center gap-2">
					<select
						value={relation}
						onChange={(e) => setRelation(e.target.value as JobDateRelation)}
						className={SELECT_CLS}
					>
						{RELATION_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>
								{t(o.labelKey as any, o.fallback)}
							</option>
						))}
					</select>
					<select
						value={whenKind}
						onChange={(e) => setWhenKind(e.target.value as WhenKind)}
						className={SELECT_CLS}
					>
						{WHEN_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>
								{t(o.labelKey as any, o.fallback)}
							</option>
						))}
					</select>
				</div>

				{whenKind === "date" && (
					<input
						type="date"
						className={`mt-2 ${DATE_CLS}`}
						value={singleDate ? singleDate.slice(0, 10) : ""}
						onChange={(e) => setSingleDate(e.target.value || undefined)}
					/>
				)}

				{whenKind === "range" && (
					<div className="mt-2 flex items-center gap-2">
						<input
							type="date"
							className={DATE_CLS}
							value={rangeStart ? rangeStart.slice(0, 10) : ""}
							onChange={(e) =>
								setRange({
									start: e.target.value ? new Date(e.target.value).toISOString() : undefined,
								})
							}
						/>
						<span className="text-gray-400">–</span>
						<input
							type="date"
							className={DATE_CLS}
							value={rangeEnd ? rangeEnd.slice(0, 10) : ""}
							onChange={(e) =>
								setRange({
									end: e.target.value
										? new Date(e.target.value + "T23:59:59").toISOString()
										: undefined,
								})
							}
						/>
					</div>
				)}
			</Section>

			{/* Link to the single "Jobbfilter" KB article explaining every filter
			    section. Sits inside FilterSheetFrame, so it opens the help sidebar
			    on the left (next to the filters) via KnowledgeBaseSideContext. */}
			<div className="border-t border-border px-5 py-4">
				<KnowledgeBaseTrigger tag="job-filters">
					<span className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline">
						<HelpCircle className="h-4 w-4" />
						{t("job.filters.readMore" as any, "Läs mer om filtren")}
					</span>
				</KnowledgeBaseTrigger>
			</div>
		</>
	);
};

export default JobFilterBuilder;
